import { useCallback, useEffect, useRef, useState } from 'react';

// Minimal typings for the (prefixed) Web Speech API — not in lib.dom for all TS
// versions, so we declare the shape we use.
interface SpeechRecognitionAlternativeLike {
  transcript: string;
}
interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0: SpeechRecognitionAlternativeLike;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: { length: number; [index: number]: SpeechRecognitionResultLike };
}
interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

const getRecognitionCtor = (): SpeechRecognitionCtor | null => {
  const w = window as unknown as {
    webkitSpeechRecognition?: SpeechRecognitionCtor;
    SpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.webkitSpeechRecognition || w.SpeechRecognition || null;
};

export interface UseMicCaptureResult {
  supported: boolean;
  listening: boolean;
  error: string | null;
  // Latest interim (not-yet-final) hypothesis, live-updating as the user speaks.
  // Empty string when there is no pending partial. Callers render this for
  // instant feedback but must NOT persist it — only finals become segments.
  interim: string;
  start: () => void;
  stop: () => void;
}

// Captures speech via the browser Web Speech API. Interim hypotheses stream to
// `interim` for instant on-screen feedback (removes the end-of-utterance lag);
// each finalized utterance is forwarded to onFinalResult. Degrades gracefully:
// supported=false when the API is unavailable so callers can hide/disable the
// mic control.
// The optional speaker parameter keeps a single callback signature across both
// mic backends; the Web Speech API has no diarization, so it never supplies one.
export const useMicCapture = (
  onFinalResult: (text: string, speaker?: string) => void
): UseMicCaptureResult => {
  const ctorRef = useRef<SpeechRecognitionCtor | null>(getRecognitionCtor());
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const callbackRef = useRef(onFinalResult);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [interim, setInterim] = useState('');
  const wantListeningRef = useRef(false);

  useEffect(() => {
    callbackRef.current = onFinalResult;
  }, [onFinalResult]);

  const supported = ctorRef.current !== null;

  const start = useCallback(() => {
    if (!ctorRef.current || recognitionRef.current) return;
    setError(null);
    const recognition = new ctorRef.current();
    recognition.continuous = true;
    // Interim results surface partial hypotheses immediately instead of waiting
    // for the engine to detect end-of-speech — this is the main lag reduction.
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let pendingInterim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const text = result[0].transcript.trim();
        if (result.isFinal) {
          if (text) callbackRef.current(text);
        } else if (text) {
          pendingInterim = pendingInterim ? `${pendingInterim} ${text}` : text;
        }
      }
      setInterim(pendingInterim);
    };

    recognition.onerror = (event) => {
      setError(event.error || 'Speech recognition error');
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        wantListeningRef.current = false;
      }
    };

    recognition.onend = () => {
      // A finalized/aborted utterance clears any dangling partial.
      setInterim('');
      // Auto-restart while the user still wants to listen (recognition stops
      // itself after silence in some browsers).
      if (wantListeningRef.current && recognitionRef.current) {
        try {
          recognitionRef.current.start();
          return;
        } catch {
          // fall through to stopped state
        }
      }
      recognitionRef.current = null;
      setListening(false);
    };

    recognitionRef.current = recognition;
    wantListeningRef.current = true;
    try {
      recognition.start();
      setListening(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start microphone');
      recognitionRef.current = null;
      wantListeningRef.current = false;
    }
  }, []);

  const stop = useCallback(() => {
    wantListeningRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // ignore
      }
    }
    recognitionRef.current = null;
    setListening(false);
    setInterim('');
  }, []);

  useEffect(() => {
    return () => {
      wantListeningRef.current = false;
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {
          // ignore
        }
        recognitionRef.current = null;
      }
    };
  }, []);

  return { supported, listening, error, interim, start, stop };
};
